import { Table } from "antd";
import { useState } from "react";
import { useTranslate } from "../i18n";
import { Person } from "../model/person"
import { CrudToolbar } from "./CrudToolbar";
import { EmptyState } from "./EmptyState";
import { PageHeader } from "./PageHeader";
import { personColumns } from "./personColumns";
import { TableSkeleton } from "./TableSkeleton";

/**
 * The list every people section renders: header, action bar, table.
 *
 * Selecting a row and opening it are two moves rather than one — the row is
 * picked with a click, the toolbar then names it and offers Open — because the
 * same selection is what a destructive action would work on.
 */
export const PersonList = (props: {
    persons: Person[],
    title: string,
    /** Eyebrow above the title; the navigation group by default. */
    eyebrow?: string,
    loading?: boolean,
    onEditPerson: (id?: string) => void,
    onReload?: () => void,
    onSearch?: (query: string) => void
}) => {

    const t = useTranslate();
    const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");

    const selectedPerson = props.persons.find(person => person.id === selectedPersonId);

    const columns = personColumns(t, { drill: true });

    const onRow = (person: Person) => {
        return {
            onClick: () => setSelectedPersonId(person.id),
            onDoubleClick: () => props.onEditPerson(person.id)
        }
    }

    const getRowClassName = (person: Person) => {
        return selectedPersonId === person.id ? 'bg-selected' : '';
    }

    const onSearch = () => {
        props.onSearch?.(searchTerm);
    }

    return (<>
        <PageHeader
            eyebrow={props.eyebrow ?? t("nav.group.clinic")}
            title={props.title}
            count={props.loading === true ? undefined : props.persons.length}
        />

        {props.loading === true
            ? <TableSkeleton />
            : <>
                <CrudToolbar
                    hasSelection={selectedPerson != null}
                    selectionLabel={
                        [selectedPerson?.firstName, selectedPerson?.lastName].filter(Boolean).join(" ")
                    }
                    onClearSelection={() => setSelectedPersonId(null)}
                    onEdit={() => props.onEditPerson(selectedPersonId!)}
                    onAdd={() => props.onEditPerson('new')}
                    onReload={props.onReload}
                    search={props.onSearch != null ? {
                        placeholder: t("list.search.person"),
                        value: searchTerm,
                        onChange: setSearchTerm,
                        onSearch
                    } : undefined}
                />
                <div className="card card--flush">
                    <Table
                        rowKey={(person: Person) => person.id}
                        rowClassName={getRowClassName}
                        onRow={onRow}
                        dataSource={props.persons}
                        columns={columns}
                        locale={{ emptyText: <EmptyState /> }}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </>}
    </>);
}
